/**
 * Settings sheet.
 *
 * Every accessibility escape hatch the design defines has to be reachable from
 * here, or it does not exist. Previously there was no settings screen at all,
 * which made the whole accessibility layer theoretical.
 */
import { useEffect, useRef } from 'react';
import { prefersReducedMotion, type Effects, type Prefs, type TextSpeed } from './prefs.js';

const EFFECTS: Array<{ value: Effects; label: string; hint: string }> = [
  { value: 'off', label: 'Выключены', hint: 'Чистое изображение, без CRT' },
  { value: 'reduced', label: 'Умеренные', hint: 'Статичные помехи, без мерцания и вспышек' },
  { value: 'full', label: 'Полные', hint: 'Со вспышками и глитчами' },
];

const SPEEDS: Array<{ value: TextSpeed; label: string }> = [
  { value: 'slow', label: 'Медленно' },
  { value: 'normal', label: 'Обычно' },
  { value: 'fast', label: 'Быстро' },
  { value: 'instant', label: 'Мгновенно' },
];

const SCALES: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Обычный' },
  { value: 1.25, label: 'Крупный' },
  { value: 1.5, label: 'Очень крупный' },
];

export function Settings({
  prefs,
  onChange,
  onClose,
}: {
  prefs: Prefs;
  onChange: (next: Prefs) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = prefersReducedMotion();

  // Focus trap + Escape. A modal a keyboard user cannot leave is worse than none.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('button')?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const items = ref.current?.querySelectorAll<HTMLElement>('button:not(:disabled)');
      if (!items?.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      prev?.focus?.();
    };
  }, [onClose]);

  const Row = <T,>({
    legend,
    options,
    current,
    set,
    disabledValue,
    disabledHint,
  }: {
    legend: string;
    options: Array<{ value: T; label: string; hint?: string }>;
    current: T;
    set: (v: T) => void;
    disabledValue?: T;
    disabledHint?: string;
  }) => (
    <fieldset className="set-group">
      <legend>{legend}</legend>
      <div className="set-opts">
        {options.map((o) => {
          const isDisabled = disabledValue !== undefined && o.value === disabledValue;
          return (
            <button
              key={String(o.value)}
              className={`set-opt${current === o.value ? ' on' : ''}`}
              aria-pressed={current === o.value}
              disabled={isDisabled}
              onClick={() => set(o.value)}
            >
              <span>{o.label}</span>
              {o.hint && <small>{o.hint}</small>}
            </button>
          );
        })}
      </div>
      {disabledHint && <p className="set-hint">{disabledHint}</p>}
    </fieldset>
  );

  return (
    <div className="set-backdrop" role="dialog" aria-modal="true" aria-labelledby="set-h" ref={ref}>
      <div className="set-sheet">
        <header className="set-top">
          <h1 id="set-h">Настройки</h1>
          <button className="set-close" onClick={onClose} aria-label="Закрыть настройки">×</button>
        </header>

        <Row
          legend="Эффекты"
          options={EFFECTS}
          current={prefs.effects}
          set={(v) => onChange({ ...prefs, effects: v })}
          {...(reduced ? { disabledValue: 'full' as Effects, disabledHint: 'В системе включено «уменьшение движения», поэтому вспышки недоступны.' } : {})}
        />

        <Row
          legend="Скорость текста"
          options={SPEEDS}
          current={prefs.textSpeed}
          set={(v) => onChange({ ...prefs, textSpeed: v })}
        />

        <Row
          legend="Размер текста"
          options={SCALES}
          current={prefs.textScale}
          set={(v) => onChange({ ...prefs, textScale: v })}
        />
      </div>
    </div>
  );
}
